<template>
    <vx-modal v-model:visible="visible" size="lg" padding="standard">
        <template v-slot:header>Assign to a campaign</template>
        <template v-slot:subheader>{{ formatPhoneNumber(props.phoneNumber) }}</template>

        <div class="flex flex-col gap-4 text-sm text-slate-800">
            <page-loading v-if="loadingLive"></page-loading>
            <template v-else-if="live">
                <vx-alert v-if="null === live.bandwidth" color="danger" :closeable="false" data-test="not-held">
                    Bandwidth does not hold this number
                </vx-alert>
                <div v-else data-test="live-assignment">
                    Bandwidth: {{ live.bandwidth.campaign_id ?? 'unassigned' }} ·
                    {{ live.bandwidth.campaign_fully_provisioned ? 'fully provisioned' : 'provisioning' }}
                </div>
                <vx-alert v-if="live.bandwidth?.campaign_id" color="info" :closeable="false" data-test="move-note">
                    Moving from {{ live.bandwidth.campaign_id }}; the current assignment is replaced
                </vx-alert>
            </template>

            <div data-test="campaign-picker">
                <vx-label size="md">Campaign</vx-label>
                <vx-selectable
                    v-model="selectedCampaignId"
                    size="lg"
                    placeholder="Search DCA-complete TCR campaigns"
                    :reduce="(option: CampaignOption) => option.value"
                    :loading="loadingCampaigns"
                    :options="campaignOptions"
                    @search="searchCampaigns"
                    :clearable="false"
                    :error="pickerError"
                ></vx-selectable>
            </div>

            <template v-if="live">
                <div>
                    <div class="font-bold mb-1">Recorded orders</div>
                    <div v-if="!live.orders.length" class="text-slate-500" data-test="order-history">
                        No orders recorded for this number yet.
                    </div>
                    <div v-else class="flex flex-col gap-1" data-test="order-history">
                        <div
                            v-for="order in live.orders"
                            :key="order.order_id"
                            class="flex flex-wrap items-center gap-2"
                            data-test="order-history-row"
                        >
                            <span class="font-mono">{{ order.order_id }}</span>
                            <span>{{ order.campaign_id ?? 'no campaign' }}</span>
                            <vx-badge :color="phoneNumberOrderStatusColors[order.status]" size="xs">
                                {{ phoneNumberOrderStatusLabels[order.status] }}
                            </vx-badge>
                            <span v-if="null !== order.error_description" class="text-rose-600">
                                {{ order.error_description }}
                                <span v-if="null !== order.error_code" class="font-mono">{{ order.error_code }}</span>
                            </span>
                            <span class="text-slate-500" :title="shortDateTime(order.settled_at ?? order.placed_at)">
                                {{ fromNow(order.settled_at ?? order.placed_at) }}
                            </span>
                        </div>
                    </div>
                </div>

                <div>
                    <div class="font-bold mb-1">Bandwidth's orders for this number</div>
                    <vx-alert
                        v-if="live.bandwidth_orders.error"
                        color="danger"
                        :closeable="false"
                        data-test="bandwidth-orders-error"
                    >
                        {{ live.bandwidth_orders.error }}
                    </vx-alert>
                    <div
                        v-else-if="!live.bandwidth_orders.data.length"
                        class="text-slate-500"
                        data-test="bandwidth-orders"
                    >
                        Bandwidth lists no orders for this number.
                    </div>
                    <div v-else class="flex flex-col gap-1" data-test="bandwidth-orders">
                        <div
                            v-for="order in live.bandwidth_orders.data"
                            :key="order.order_id"
                            class="flex flex-wrap items-center gap-2"
                            data-test="bandwidth-order-row"
                        >
                            <span class="font-mono">{{ order.order_id }}</span>
                            <span>{{ tnOptionOrderStatusLabels[order.status] }}</span>
                            <span>{{ order.campaign_id ?? 'no campaign' }}</span>
                            <span
                                v-for="(error, index) in order.errors"
                                :key="index"
                                class="text-rose-600"
                                data-test="bandwidth-order-error"
                            >
                                {{ error.code }} · {{ error.description }}
                            </span>
                            <span
                                v-if="order.modified_at ?? order.created_at"
                                class="text-slate-500"
                                :title="shortDateTime(order.modified_at ?? order.created_at)"
                            >
                                {{ fromNow(order.modified_at ?? order.created_at) }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-assign"
                    :disabled="confirmDisabled"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Assign
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxAlert, VxBadge, VxButton, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, ref, watch } from 'vue';
import { formatPhoneNumber } from '~/components/filters';
import PageLoading from '~/components/general/PageLoading.vue';
import {
    phoneNumberOrderStatusColors,
    phoneNumberOrderStatusLabels,
    tnOptionOrderStatusLabels,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcClient, adminTenDlcRegistrationClient } from '~/services';
import type { TcrCampaign } from '~/services/adminTenDlcClient.types';
import type { BandwidthTnOptionOrder, TcrLivePhoneNumber } from '~/services/adminTenDlcRegistrationClient.types';
import { fromNow, shortDateTime } from '~/utils/date';
import debounce from '~/utils/debounce';
import { catchRequestError } from '~/utils/request';
import { joinFieldErrors } from '~/utils/validation';

type CampaignOption = { value: string; label: string };

const props = defineProps<{ phoneNumber: string }>();
const emit = defineEmits<{ placed: [order: BandwidthTnOptionOrder] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { $externalResults, catchError } = useFormRequestHandler();
const toasted = useToasted();

const live = ref<TcrLivePhoneNumber | null>(null);
const loadingLive = ref(false);
const campaignResults = ref<TcrCampaign[]>([]);
const selectedCampaignId = ref<string>();
const loadingCampaigns = ref(false);

const currentCampaignId = computed(() => live.value?.bandwidth?.campaign_id ?? null);
const campaignOptions = computed<CampaignOption[]>(() =>
    campaignResults.value
        .filter((campaign) => campaign.campaign_id !== currentCampaignId.value)
        .map((campaign) => ({
            value: campaign.campaign_id,
            label: `${campaign.campaign_id} · ${campaign.brand_display_name ?? campaign.brand_id ?? '—'}`,
        }))
);

const campaignsCancelKey = Symbol('kiosk-tcr-number-assign-campaigns');

const searchCampaigns = debounce((query?: string) => {
    loadingCampaigns.value = true;

    adminTenDlcClient
        .getTcrCampaigns(
            { query, source: 'tcr', statuses: ['active'], dca_complete: 1 },
            { cancelKey: campaignsCancelKey }
        )
        .then((response) => {
            campaignResults.value = response.data.data;
            loadingCampaigns.value = false;
        })
        .catch((e) => {
            if (isCancel(e)) {
                return;
            }

            loadingCampaigns.value = false;
            console.error(e);
            toasted.global.platform_error();
        });
}, 300);

const loadLive = () => {
    loadingLive.value = true;

    adminTenDlcRegistrationClient
        .getPhoneNumber(props.phoneNumber)
        .then((response) => {
            live.value = response.data;
        })
        .catch((e) => {
            toasted.error(catchRequestError(e), { sticky: true });
        })
        .finally(() => {
            loadingLive.value = false;
        });
};

const pickerError = computed(() => joinFieldErrors($externalResults.value, 'phone_numbers'));

const confirmDisabled = computed(
    () => null === (live.value?.bandwidth ?? null) || undefined === selectedCampaignId.value
);

const submitting = ref(false);

watch(
    visible,
    (open) => {
        if (!open) {
            return;
        }

        $externalResults.value = {};
        selectedCampaignId.value = undefined;
        live.value = null;
        loadLive();
        searchCampaigns();
    },
    { immediate: true }
);

const confirm = async () => {
    if (submitting.value || undefined === selectedCampaignId.value) {
        return;
    }

    submitting.value = true;

    try {
        const { data } = await adminTenDlcRegistrationClient.assignCampaignPhoneNumbers(selectedCampaignId.value, {
            phone_numbers: [props.phoneNumber],
        });

        emit('placed', data);
        visible.value = false;
    } catch (e) {
        await catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
